import React, { useState, useEffect } from 'react';
import presetApi from '@services/presetApi.jsx';
import themeAngleMappingApi from '@services/themeAngleMappingApi.jsx';
import '@assets/js/common/table';
import AngleContentsAdd from '@assets/js/modals/AngleContentsAdd';
import { getTimerRedirectOptions, migrateTimerRedirectValue } from '../utils/pageNavigationUtils';

const PresetAngleSetting = ({ preset, presetPageMasters }) => {
    const VITE_ADMIN_BACKEND_API_URL = import.meta.env.VITE_ADMIN_BACKEND_API_URL;

    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingAngles, setIsLoadingAngles] = useState(false);

    const searchParams = new URLSearchParams(location.search);
    const presetId = searchParams.get('presetId');
    const [presetAnglePage, setPresetAnglePage] = useState(null);

    // 폼 데이터 상태
    const [formData, setFormData] = useState({
        enablePhotoAngle: true,
        enableVideoAngle: true,
        timerSeconds: 10,
        timerTimeoutRedirectUrl: 'start',
        autoSelectAngleId: null,
        isActive: true
    });

    // 타이머 관련
    const [timer, setTimer] = useState(10);
    const [timeOverPage, setTimeOverPage] = useState('start');
    const [angleFilter, setAngleFilter] = useState('all');
    
    // 타이머 리다이렉트 옵션
    const [timerRedirectOptions, setTimerRedirectOptions] = useState([]);

    // 테마 앵글 목록
    const [themeAngles, setThemeAngles] = useState([]);
    const [angles, setAngles] = useState([]);

    // 테이블 선택 관련
    const [selectAll, setSelectAll] = useState(false);

    // 모달 상태 관리
    const [showAddModal, setShowAddModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [editTargetAngle, setEditTargetAngle] = useState(null);
    const [modalMode, setModalMode] = useState('add');
    const [showDetailModal, setShowDetailModal] = useState(false);
    const [currentAngle, setCurrentAngle] = useState(null);

    // 페이지 데이터 로드
    useEffect(() => {
        loadAngleSelectPageData();
    }, [presetId]);

    useEffect(() => {
        if(presetAnglePage?.themeId
            && presetAnglePage?.themePageMasterId)
        {
            loadThemeAngles();
        }
    }, [presetAnglePage]);
    
    // 타이머 리다이렉트 옵션 업데이트
    useEffect(() => {
        if (presetPageMasters && presetPageMasters.length > 0) {
            const options = getTimerRedirectOptions('angle', presetPageMasters);
            setTimerRedirectOptions(options);
        }
    }, [presetPageMasters]);

    // 앵글 선택 페이지 데이터 로드
    const loadAngleSelectPageData = async () => {
        try {
            setIsLoading(true);
            const anglePage = await presetApi.getAngleSelectPage(presetId);

            setPresetAnglePage(anglePage);
            
            // 기존 timerTimeoutRedirectUrl 값을 새로운 형식으로 마이그레이션
            const migratedRedirectValue = migrateTimerRedirectValue(
                anglePage.timerTimeoutRedirectUrl, 
                'angle'
            );

            setFormData({
                enablePhotoAngle: anglePage.enablePhotoAngle ?? true,
                enableVideoAngle: anglePage.enableVideoAngle ?? true,
                timerSeconds: anglePage.timerSeconds ?? 10,
                timerTimeoutRedirectUrl: migratedRedirectValue,
                autoSelectAngleId: anglePage.autoSelectAngleId ?? null,
                isActive: anglePage.isActive ?? true
            });
            
            setTimer(anglePage.timerSeconds ?? 10);
            setTimeOverPage(migratedRedirectValue);
        } catch (error) {
            console.error('앵글 선택 페이지 데이터 로드 실패:', error);
            // 에러 시 기본값 유지
        } finally {
            setIsLoading(false);
        }
    };

    // 테마 앵글 목록 로드
    const loadThemeAngles = async () => {
        if (!preset?.themeId) return;

        console.log(preset);

        try {
            setIsLoadingAngles(true);

            const mappings = await themeAngleMappingApi.getThemeAngleMappings(presetAnglePage?.themeId, presetAnglePage?.themePageMasterId);

            const formattedAngles = mappings.map((mapping, index) => ({
                id: mapping.id,
                mappingId: mapping.id,
                cameraAngleId: mapping.cameraAngleId,
                name: mapping.angleName,
                type: mapping.formattedAngleType || mapping.angleTypeDescription,
                angleType: mapping.angleTypeCode,
                selected: false,
                motionData: mapping.angleDataViewUrl, // angleData 관련 URL
                // 리소스 ID 저장 (이미지 URL 구성용)
                customDefaultImageResourceId: mapping.customDefaultImageResourceId,
                customHoverImageResourceId: mapping.customHoverImageResourceId,
                angleDataResourceId: mapping.angleDataResourceId,
                displayOrder: mapping.displayOrder,
                isActive: mapping.isActive,
                isPhoto: mapping.isPhoto,
                isVideo: mapping.isVideo,
                isDeployed: mapping.angleIsDeployed,
                startDatetime: mapping.angleStartDatetime,
                endDatetime: mapping.angleEndDatetime,
                themeId: mapping.themeId
            }));

            setThemeAngles(formattedAngles);
            // Modified: 2025-01-27 [현재 시간 추가 필요] - 필터링 로직 추가
            // 초기 로드 시 필터 적용
            applyAngleFilter(formattedAngles, angleFilter);
            
            // 앵글이 있고 현재 선택된 값이 없으면 첫 번째 앵글 자동 선택
            if (formattedAngles.length > 0 && !formData.autoSelectAngleId) {
                setFormData(prev => ({
                    ...prev,
                    autoSelectAngleId: formattedAngles[0].id
                }));
            }
        } catch (error) {
            console.error('테마 앵글 목록 로드 실패:', error);
            setThemeAngles([]);
            setAngles([]);
        } finally {
            setIsLoadingAngles(false);
        }
    };

    // Modified: 2025-01-27 [현재 시간 추가 필요] - 앵글 필터링 함수 추가
    const applyAngleFilter = (anglesList, filterValue) => {
        if (filterValue === 'all') {
            setAngles(anglesList);
        } else if (filterValue === 'photo') {
            setAngles(anglesList.filter(angle => angle.isPhoto && !angle.isVideo));
        } else if (filterValue === 'video') {
            setAngles(anglesList.filter(angle => angle.isVideo && !angle.isPhoto));
        } else if (filterValue === 'combined') {
            setAngles(anglesList.filter(angle => angle.isPhoto && angle.isVideo));
        } else {
            setAngles(anglesList);
        }
    };

    // Modified: 2025-01-27 [현재 시간 추가 필요] - 필터 변경 시 필터링 적용
    useEffect(() => {
        if (themeAngles.length > 0) {
            applyAngleFilter(themeAngles, angleFilter);
        }
    }, [angleFilter, themeAngles]);

    // 앵글 타입 표시 변환
    const getAngleTypeDisplay = (angleType) => {
        switch (angleType) {
            case 'PHOTO':
                return '사진';
            case 'VIDEO':
                return '동영상';
            case 'BOTH':
                return '동영상 + 사진';
            default:
                return angleType;
        }
    };

    // 앵글 타입 토글 핸들러
    const handleAngleTypeToggle = (type) => {
        const newFormData = {
            ...formData,
            [type]: !formData[type]
        };
        setFormData(newFormData);
    };

    // 타이머 조절 핸들러
    const handleTimerChange = (action) => {
        let newTimer;
        if (action === 'increase') {
            newTimer = Math.min(timer + 1, 60);
        } else {
            newTimer = Math.max(timer - 1, 1);
        }
        
        setTimer(newTimer);
        setFormData({
            ...formData,
            timerSeconds: newTimer
        });
    };

    // 타임 오버 페이지 변경 핸들러
    const handleTimeOverPageChange = (e) => {
        const newPage = e.target.value;
        setTimeOverPage(newPage);
        setFormData({
            ...formData,
            timerTimeoutRedirectUrl: newPage
        });
    };

    // 자동 선택 앵글 변경 핸들러
    const handleAutoSelectAngleChange = (e) => {
        const angleId = e.target.value ? parseInt(e.target.value) : null;
        // 값이 비어있고 앵글 목록이 있으면 첫 번째 앵글 선택
        const finalAngleId = angleId || (themeAngles.length > 0 ? themeAngles[0].id : null);
        setFormData({
            ...formData,
            autoSelectAngleId: finalAngleId
        });
    };

    // 체크박스 선택 핸들러
    const handleCheckboxChange = (id) => {
        setAngles(prevAngles =>
            prevAngles.map(angle =>
                angle.id === id ? { ...angle, selected: !angle.selected } : angle
            )
        );
    };

    // 전체 선택 핸들러
    const handleSelectAll = () => {
        setSelectAll(!selectAll);
        setAngles(prevAngles =>
            prevAngles.map(angle => ({ ...angle, selected: !selectAll }))
        );
    };

    // 상세정보 아이콘 클릭 핸들러
    const handleInfoClick = (angle) => {
        setCurrentAngle(angle);
        setShowDetailModal(true);
    };

    // 모달 관련 핸들러들
    const handleCloseModal = () => {
        setShowAddModal(false);
        document.body.classList.remove('modal-open');
    };

    const handleSaveAngle = (newAngle) => {
        setAngles(prev => [...prev, newAngle]);
        setShowAddModal(false);
    };

    const handleCloseDeleteModal = () => {
        setShowDeleteModal(false);
        document.body.classList.remove('modal-open');
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditTargetAngle(null);
        document.body.classList.remove('modal-open');
    };

    const handleSaveEdit = (updatedAngle) => {
        setAngles(prevAngles =>
            prevAngles.map(angle =>
                angle.id === updatedAngle.id ? updatedAngle : angle
            )
        );
        handleCloseEditModal();
    };

    // 저장 이벤트 리스너
    useEffect(() => {
        const eventName = `savePagePreset_angle`;

        const saveHandler = async (event) => {
            if (!preset?.id) {
                alert("프리셋 정보가 없습니다.");
                return;
            }

            try {
                setIsLoading(true);

                // 저장 전 자동 선택 앵글 검증
                let autoSelectAngleId = formData.autoSelectAngleId;
                if (!autoSelectAngleId && themeAngles.length > 0) {
                    autoSelectAngleId = themeAngles[0].id;
                }

                const dataToSave = {
                    presetId: event.detail.presetId,
                    enablePhotoAngle: formData.enablePhotoAngle,
                    enableVideoAngle: formData.enableVideoAngle,
                    timerSeconds: formData.timerSeconds,
                    timerTimeoutRedirectUrl: formData.timerTimeoutRedirectUrl,
                    autoSelectAngleId: autoSelectAngleId,
                    isActive: formData.isActive
                };
                
                const response = await presetApi.saveAngleSelectPage(event.detail.presetId, dataToSave);
                console.log("앵글 선택 페이지 저장 성공:", response);

                // 저장 후 최신 데이터로 화면 갱신
                if (response) {
                    setFormData({
                        enablePhotoAngle: response.enablePhotoAngle ?? true,
                        enableVideoAngle: response.enableVideoAngle ?? true,
                        timerSeconds: response.timerSeconds ?? 10,
                        timerTimeoutRedirectUrl: response.timerTimeoutRedirectUrl ?? 'start',
                        autoSelectAngleId: response.autoSelectAngleId ?? null,
                        isActive: response.isActive ?? true
                    });
                    setPresetAnglePage(response);
                }

            } catch (error) {
                console.error("앵글 선택 페이지 저장 실패:", error);
                alert("앵글 선택 페이지 저장 중 오류가 발생했습니다.");
            } finally {
                setIsLoading(false);
            }
        };

        document.addEventListener(eventName, saveHandler);

        return () => {
            document.removeEventListener(eventName, saveHandler);
        };
    }, [preset?.id, formData]);

    // 테이블 초기화
    // Modified: 2025-01-27 [현재 시간 추가 필요] - 필터 기능 활성화로 filterSelector 복원
    useEffect(() => {
        if (window.tableManager) {
            window.tableManager.initTable({
                tableSelector: '.common-table',
                filterSelector: '#angle-filter',
                columnWidthOptions: {
                    equalWidth: false,
                    firstColumnWidth: 5,
                    lastColumnWidth: 5,
                    fixedColumns: {
                        1: 10,  // NO 열
                        2: 20,  // 앵글명 열
                        3: 15,  // 앵글 타입 열
                        4: 10,  // 상세정보 열
                        5: 10,  // 수정 열
                        6: 10   // 삭제 열
                    }
                }
            });
        }
    }, [angles]);

    if (isLoading) {
        return <div className="preset-settings">로딩 중...</div>;
    }

    return (
        <div className="preset-settings">
            {/* 앵글 타입 설정 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">앵글 타입 설정</h4>
                <div className="settings-row">
                    <div className="settings-label">사진</div>
                    <div className="toggle-switch-container">
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                checked={formData.enablePhotoAngle}
                                onChange={() => handleAngleTypeToggle('enablePhotoAngle')}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">동영상</div>
                    <div className="toggle-switch-container">
                        <label className="toggle-switch">
                            <input 
                                type="checkbox" 
                                checked={formData.enableVideoAngle}
                                onChange={() => handleAngleTypeToggle('enableVideoAngle')}
                            />
                            <span className="toggle-slider"></span>
                        </label>
                    </div>
                </div>
            </div>

            {/* 타이머 설정 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">타이머</h4>
                <div className="settings-row">
                    <div className="settings-label">타이머 시간</div>
                    <div className="timer-control-container">
                        <button 
                            className="timer-btn" 
                            onClick={() => handleTimerChange('decrease')}
                        >
                            <i className="ri-arrow-down-s-fill"></i>
                        </button>
                        <input 
                            type="text" 
                            className="timer-input" 
                            value={timer}
                            readOnly
                        />
                        <button 
                            className="timer-btn" 
                            onClick={() => handleTimerChange('increase')}
                        >
                            <i className="ri-arrow-up-s-fill"></i>
                        </button>
                        <span className="timer-unit">초</span>
                    </div>
                </div>
                <div className="settings-row">
                    <div className="settings-label">타임 오버 이동 페이지</div>
                    <div className="page-select-container">
                        <select 
                            className="page-select"
                            value={timeOverPage}
                            onChange={handleTimeOverPageChange}
                        >
                            {timerRedirectOptions.map(option => (
                                <option 
                                    key={option.value} 
                                    value={option.value}
                                    disabled={!option.available}
                                >
                                    {option.label}
                                </option>
                            ))}
                        </select>
                        <i className="ri-arrow-down-s-line select-arrow"></i>
                    </div>
                </div>
            </div>

            {/* 페이지 스킵 시 앵글 자동 선택 섹션 */}
            <div className="settings-box">
                <h4 className="settings-title">페이지 스킵 시 앵글 자동 선택</h4>
                <div className="option-select-container">
                    <select 
                        className="option-select"
                        value={formData.autoSelectAngleId || (themeAngles.length > 0 ? themeAngles[0].id : '')}
                        onChange={handleAutoSelectAngleChange}
                    >
                        {themeAngles.length > 0 ? (
                            themeAngles.map(angle => (
                                <option key={angle.id} value={angle.id}>
                                    {angle.name} ({angle.type})
                                </option>
                            ))
                        ) : (
                            <option value="">없음</option>
                        )}
                    </select>
                    <i className="ri-arrow-down-s-line select-arrow"></i>
                </div>
            </div>

            {/* 앵글 데이터 섹션 헤더 */}
            {/* Modified: 2025-01-27 [현재 시간 추가 필요] - 필터 기능 활성화 */}
            <div className="data-section-header">
                <div className="data-section-title">
                    <h4 className="section-title">앵글목록</h4>
                    {isLoadingAngles && <span className="loading-text">(로딩 중...)</span>}
                </div>
                <div className="filter-dropdown-container">
                    <div className="filter-dropdown">
                        <select 
                            id="angle-filter"
                            value={angleFilter}
                            onChange={(e) => setAngleFilter(e.target.value)}
                        >
                            <option value="all">전체</option>
                            <option value="photo">사진</option>
                            <option value="video">동영상</option>
                            <option value="combined">동영상 + 사진</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* 데이터 테이블 섹션 */}
            <div className="common-table-container">
                <table className="common-table">
                    <thead>
                        <tr>
                            {/*<th>*/}
                            {/*    <div className="checkbox-container">*/}
                            {/*        <input */}
                            {/*            type="checkbox" */}
                            {/*            id="select-all" */}
                            {/*            checked={selectAll}*/}
                            {/*            onChange={handleSelectAll}*/}
                            {/*        />*/}
                            {/*        <label htmlFor="select-all" className="custom-checkbox"></label>*/}
                            {/*    </div>*/}
                            {/*</th>*/}
                            <th>NO</th>
                            <th>앵글명</th>
                            <th>앵글 타입</th>
                            <th>상세정보</th>
                        </tr>
                    </thead>
                    <tbody>
                        {angles.length === 0 ? (
                            <tr>
                                <td colSpan="4" className="text-center">
                                    {isLoadingAngles ? '앵글 목록을 로딩 중입니다...' : '등록된 앵글이 없습니다.'}
                                </td>
                            </tr>
                        ) : (
                            angles.map((angle, index) => (
                                <tr key={angle.id}>
                                    {/*<td>*/}
                                    {/*    <div className="checkbox-container">*/}
                                    {/*        <input */}
                                    {/*            type="checkbox" */}
                                    {/*            id={`angle-${angle.id}`}*/}
                                    {/*            checked={angle.selected}*/}
                                    {/*            onChange={() => handleCheckboxChange(angle.id)}*/}
                                    {/*        />*/}
                                    {/*        <label htmlFor={`angle-${angle.id}`} className="custom-checkbox"></label>*/}
                                    {/*    </div>*/}
                                    {/*</td>*/}
                                    <td>{index + 1}</td>
                                    <td>{angle.name}</td>
                                    <td>{angle.type}</td>
                                    <td>
                                        <button className="info-btn" onClick={() => handleInfoClick(angle)}>
                                            <i className="ri-information-line"></i>
                                        </button>
                                    </td>
                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* 모달들 */}
            {showAddModal && (
                <AngleContentsAdd 
                    onClose={handleCloseModal}
                    onSave={handleSaveAngle}
                    initialData={editTargetAngle}
                    isEditMode={modalMode === 'edit'}
                />
            )}

            {showDeleteModal && (
                <div className="modal-overlay active">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">앵글 삭제</h3>
                            <button className="modal-close" onClick={handleCloseDeleteModal}>
                                <i className="ri-close-line"></i>
                            </button>
                        </div>
                        <div className="modal-body">
                            <p>이 앵글을 삭제하시겠습니까?</p>
                        </div>
                        <div className="modal-footer">
                        </div>
                    </div>
                </div>
            )}

            {showEditModal && (
                <AngleContentsAdd 
                    onClose={handleCloseEditModal}
                    onSave={handleSaveEdit}
                    initialData={editTargetAngle}
                    isEditMode={true}
                />
            )}

            {showDetailModal && currentAngle && (
                <div className="modal-overlay active" id="angle-detail-modal">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3 className="modal-title">앵글 상세정보</h3>
                            <button className="modal-close" onClick={() => setShowDetailModal(false)}>&times;</button>
                        </div>
                        <div className="modal-body">
                            <div className="detail-item-container">
                                <span className="detail-label">앵글명</span>
                                <span className="detail-content">{currentAngle.name}</span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">타입</span>
                                <span className="detail-content">{currentAngle.type}</span>
                            </div>
                            <div className="detail-item-container">
                                <span className="detail-label">모션 미리보기</span>
                                <div className="angle-motion">
                                    <img src={currentAngle.motionData || "angleMotions/angle_motion.png"} alt="angle_motion" />
                                </div>
                            </div>
                            <div className="detail-item-container">
                                <div className="detail-row">
                                    <div className="detail-column">
                                        <span className="detail-label">테마별 기본 이미지</span>
                                        <div className="modal-data-image">
                                            {currentAngle.customDefaultImageResourceId ? (
                                                <img
                                                    src={`${VITE_ADMIN_BACKEND_API_URL}/resource/view/${currentAngle.customDefaultImageResourceId}`}
                                                    alt="custom_default"
                                                />
                                            ) : (
                                                <img
                                                    src="images/angleData/default_angle.png"
                                                    alt="default_image"
                                                />
                                            )}
                                        </div>
                                    </div>
                                    <div className="detail-column">
                                        <span className="detail-label">테마별 호버 이미지</span>
                                        <div className="modal-data-image">
                                            {currentAngle.customHoverImageResourceId ? (
                                                <img
                                                    src={`${VITE_ADMIN_BACKEND_API_URL}/resource/view/${currentAngle.customHoverImageResourceId}`}
                                                    alt="custom_hover"
                                                />
                                            ) : (
                                                <img
                                                    src="images/angleData/default_angle_hover.png"
                                                    alt="default_hover"
                                                />
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="modal-btn modal-btn-primary" 
                                onClick={() => setShowDetailModal(false)}
                            >
                                확인
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PresetAngleSetting;
